import { FeatureFlag } from '@ps/web-core-enums';

import type { WidgetEffectParams } from '@swf/core/store';
import { pageChange } from '@swf/core/utils/actions';
import type { BasicSportsWidgetConfig } from '@swf/sports/pages/types';
import { sportsWidgetPollingBehavior } from '@swf/sports/utils/actions';
import buildSportsRequest from '@swf/sports/utils/buildSportsRequest';
import getErrorCode from '@swf/sports/utils/getErrorCode';
import { getWidgetCancellationSignal } from '@swf/sports/utils/getWidgetCancellationSignal';
import { hasRequestAborted } from '@swf/sports/utils/hasRequestAborted';

import type { EventInfoSuccessResponse } from '../types';

import type { WidgetDispatch, WidgetSelect } from './types';

export const effects = {
  async preload(_payload: undefined, params: WidgetEffectParams<BasicSportsWidgetConfig>) {
    const { dispatch, widget, select, rootState, actions } = params;
    const widgetDispatch: WidgetDispatch = dispatch[widget.uid];
    const { hasPreloaded }: WidgetSelect = select[widget.uid];
    const {
      eventInfoRequested,
      clear,
      preloaded,
      quickSwitcherRequested,
      clearEventInfo
    }: WidgetDispatch = widgetDispatch;

    sportsWidgetPollingBehavior(params).subscribe({
      next: () => {
        eventInfoRequested();
      },
      complete: () => clear()
    });

    pageChange(actions)?.subscribe({
      next: () => {
        quickSwitcherRequested();
        clearEventInfo();
      },
      complete: () => clear()
    });

    if (!hasPreloaded(rootState)) {
      await eventInfoRequested();
      preloaded();
    }

    quickSwitcherRequested();
  },

  async eventInfoRequested(
    _payload: undefined,
    params: WidgetEffectParams<BasicSportsWidgetConfig>
  ): Promise<void> {
    const { dispatch, request, widget, select, rootState } = params;
    const { eventId } = select.sports.getUrlParams(rootState);
    const sportsRequest = buildSportsRequest(request, rootState);

    const widgetDispatch: WidgetDispatch = dispatch[widget.uid];

    try {
      const {
        body: { data }
      } = await sportsRequest<EventInfoSuccessResponse>({
        path: `/event-info/${eventId}`,
        includeFeatures: [FeatureFlag.ISPDataVisualisation, FeatureFlag.ISPEnableLiveStreaming],
        signal: getWidgetCancellationSignal(params)
      });
      widgetDispatch.dataLoadSuccess(data);
    } catch (error) {
      if (hasRequestAborted(error)) return;
      widgetDispatch.dataLoadFailed(getErrorCode(error));
    }
  },

  async quickSwitcherRequested(
    _payload: undefined,
    params: WidgetEffectParams<BasicSportsWidgetConfig>
  ) {
    const { dispatch, rootState, select } = params;

    const { eventId } = select.sports.getUrlParams(rootState);

    dispatch.quickSwitcher.quickSwitcherRequested({
      hierarchyLevel: 'event',
      facetId: eventId
    });
  }
};
